import { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import LanguageSwitcher from './LanguageSwitcher';
import { pathFor } from '@/data/routes';

const NAV_KEYS = [
  { key: 'navigation:startHere', route: 'startHere' },
  { key: 'navigation:blog', route: 'blog' },
  { key: 'navigation:tools', route: 'tools' },
  { key: 'navigation:comparisons', route: 'comparisons' },
  { key: 'navigation:flights', route: 'flights' },
  { key: 'navigation:shop', route: 'shop' },
  { key: 'navigation:newsletter', route: 'newsletter' },
];

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled])';

/**
 * Slide-from-right mobile drawer with focus trap + scroll lock.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {'fr'|'en'|'es'} [props.lang='fr']
 */
export default function MobileMenu({ open, onClose, lang = 'fr' }) {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const panelRef = useRef(null);
  const lastTriggerRef = useRef(null);

  useEffect(() => {
    if (open) {
      lastTriggerRef.current = document.activeElement;
    } else if (lastTriggerRef.current && lastTriggerRef.current.focus) {
      lastTriggerRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = panel.querySelectorAll(FOCUSABLE);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    const closeBtn = panelRef.current?.querySelector('[data-close]');
    closeBtn?.focus();

    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="backdrop"
            className="fixed inset-0 z-40 bg-navy-900/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.div
            ref={panelRef}
            key="panel"
            role="dialog"
            aria-modal="true"
            aria-label={t('navigation:openMenu')}
            initial={reduce ? { opacity: 0 } : { x: '100%' }}
            animate={reduce ? { opacity: 1 } : { x: 0 }}
            exit={reduce ? { opacity: 0 } : { x: '100%' }}
            transition={{ type: 'tween', duration: 0.3, ease: 'easeOut' }}
            className="fixed inset-y-0 right-0 z-50 flex w-[88%] max-w-sm flex-col bg-navy-800 text-cream-50 shadow-card-hover dark:bg-zinc-950 dark:text-white"
          >
            {/* Bug fix mobile 2026-06-30 : la drawer faisait w-full max-w-sm
                → sur téléphones ≤384px, le backdrop est invisible et le user
                ne peut plus fermer. w-[88%] laisse toujours ~12% de backdrop
                tappable. Le bouton X a été passé de p-1.5 (~34px total) à
                p-3 min-h-11 min-w-11 pour respecter les 44px WCAG. */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-navy-700 dark:border-white/10">
              {/* V4 — label-mono JetBrains Mono pour le titre de drawer mobile
                  (cohérence avec MegaMenu desktop + Footer columns). */}
              <span className="v4-label-mono text-cream-50/80 dark:text-white/70">{t('common:siteName')}</span>
              <button
                type="button"
                data-close
                onClick={onClose}
                aria-label={t('navigation:closeMenu')}
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-btn p-3 text-cream-50 transition-colors hover:bg-navy-700 focus-visible:bg-navy-700 dark:text-white dark:hover:bg-white/10 dark:focus-visible:bg-white/10"
              >
                <X size={24} aria-hidden="true" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-6 py-6" aria-label={t('navigation:primary')}>
              <ul className="flex flex-col gap-1">
                {NAV_KEYS.map(({ key, route }) => (
                  <li key={route}>
                    <Link
                      to={pathFor(route, lang)}
                      onClick={onClose}
                      className="block rounded-btn px-3 py-3 font-serif text-2xl italic text-cream-50 transition-colors hover:bg-navy-700 focus-visible:bg-navy-700 dark:text-white dark:hover:bg-white/10 dark:focus-visible:bg-white/10"
                    >
                      {t(key)}
                    </Link>
                  </li>
                ))}
                {/* Boutique Gumroad — lien externe (page profil custom,
                    catalogue complet trilingue). Même style, nouvel onglet. */}
                <li key="gumroad-shop">
                  <a
                    href="https://insidepanama.gumroad.com"
                    target="_blank"
                    rel="noopener"
                    onClick={onClose}
                    className="block rounded-btn px-3 py-3 font-serif text-2xl italic text-cream-50 transition-colors hover:bg-navy-700 focus-visible:bg-navy-700 dark:text-white dark:hover:bg-white/10 dark:focus-visible:bg-white/10"
                  >
                    {t('navigation:gumroadShop')} ↗
                  </a>
                </li>
              </ul>
            </nav>
            <div className="border-t border-navy-700 px-6 py-5 dark:border-white/10">
              <LanguageSwitcher variant="dark" />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
